import { makeAutoObservable, runInAction } from "mobx";
import { DEFAULT_PREFERENCES } from "@/constants/maoCalculator";
import { getUserProfileNameParts } from "@/utils/getUserProfileNameParts";
import {
  parseUserMetadata,
  resolveUserIdForProfileUpdate,
} from "@/utils/realtorAgentMetadata";
import { updateUserProfile } from "@/store/userApi";

const PREF_KEYS = Object.freeze([
  "buyingAgentRate",
  "sellingAgentRate",
  "buyerClosingRate",
  "sellingClosingRate",
]);

function normalizeNumber(value) {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function pickNumber(raw, fallback) {
  const next = normalizeNumber(raw);
  return next == null ? fallback : next;
}

/**
 * Investor-only calculator preferences persisted at `user.metadata.calculator_preferences`.
 */
class InvestorPrefsStore {
  prefs = {
    buyingAgentRate: DEFAULT_PREFERENCES.buyingAgentRate,
    sellingAgentRate: DEFAULT_PREFERENCES.sellingAgentRate,
    buyerClosingRate: DEFAULT_PREFERENCES.buyerClosingRate,
    sellingClosingRate: DEFAULT_PREFERENCES.sellingClosingRate,
  };

  hasSavedPreferences = false;
  loaded = false;
  isSaving = false;
  isLoading = false;

  constructor(authStore) {
    this.authStore = authStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  loadFromProfile(userProfile, { isInvestor } = {}) {
    this.isLoading = true;
    try {
      if (!isInvestor) {
        this.hasSavedPreferences = false;
        this.loaded = true;
        return;
      }

      const metadata = parseUserMetadata(userProfile?.metadata);
      const saved = metadata?.calculator_preferences;
      const isObject = saved && typeof saved === "object" && !Array.isArray(saved);
      this.prefs = {
        buyingAgentRate: pickNumber(
          isObject ? saved.buyingAgentRate : null,
          DEFAULT_PREFERENCES.buyingAgentRate
        ),
        sellingAgentRate: pickNumber(
          isObject ? saved.sellingAgentRate : null,
          DEFAULT_PREFERENCES.sellingAgentRate
        ),
        buyerClosingRate: pickNumber(
          isObject ? saved.buyerClosingRate : null,
          DEFAULT_PREFERENCES.buyerClosingRate
        ),
        sellingClosingRate: pickNumber(
          isObject ? saved.sellingClosingRate : null,
          DEFAULT_PREFERENCES.sellingClosingRate
        ),
      };
      this.hasSavedPreferences = Boolean(isObject);
      this.loaded = true;
    } finally {
      this.isLoading = false;
    }
  }

  pickPreferenceFields(calcData) {
    const source = calcData && typeof calcData === "object" ? calcData : {};
    return {
      buyingAgentRate: pickNumber(source.buyingAgentRate, DEFAULT_PREFERENCES.buyingAgentRate),
      sellingAgentRate: pickNumber(source.sellingAgentRate, DEFAULT_PREFERENCES.sellingAgentRate),
      buyerClosingRate: pickNumber(source.buyerClosingRate, DEFAULT_PREFERENCES.buyerClosingRate),
      sellingClosingRate: pickNumber(
        source.sellingClosingRate,
        DEFAULT_PREFERENCES.sellingClosingRate
      ),
    };
  }

  async save(nextPrefs) {
    const authStore = this.authStore;
    const profile = authStore?.userProfile;
    const userId = resolveUserIdForProfileUpdate(profile);
    if (!userId) {
      return { ok: false, statusCode: "", body: null };
    }

    const prefs = this.pickPreferenceFields(nextPrefs);
    const metadata = parseUserMetadata(profile?.metadata);
    const mergedMetadata = {
      ...metadata,
      calculator_preferences: prefs,
    };

    const { first, last } = getUserProfileNameParts(profile);
    const phone = String(profile?.phone ?? "").trim();
    const email = String(profile?.email ?? authStore?.email ?? "")
      .trim()
      .toLowerCase();
    const active = typeof profile?.active === "boolean" ? profile.active : undefined;

    this.isSaving = true;
    try {
      const result = await updateUserProfile({
        userId,
        customerId: authStore?.customer ?? undefined,
        firstName: first,
        lastName: last,
        phone,
        email,
        ...(active !== undefined ? { active } : {}),
        metadata: JSON.stringify(mergedMetadata),
      });
      if (result?.ok) {
        runInAction(() => {
          this.prefs = prefs;
          this.hasSavedPreferences = true;
        });
        authStore?.patchUserProfileMetadata?.(mergedMetadata);
      }
      return result;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }
}

export { PREF_KEYS };
export default InvestorPrefsStore;
